import React, { useState } from 'react';

/**
 * Wizard — shared multi-step form component.
 *
 * Generic multi-step UX with step indicator, Back/Next navigation, and
 * per-step validation. Callers supply an array of WizardStep definitions;
 * each step owns its own render + an `isValid` predicate. Wizard handles
 * navigation, the step indicator, and button disable state.
 *
 * Reusable across Phase 7d (Contract Modification), Phase 8 (Principal
 * vs Agent wizard), Phase 11 (over-time recognition criteria).
 */

export interface WizardStep {
  key: string;
  title: string;
  description?: string;
  isValid: () => boolean;
  render: () => React.ReactNode;
}

interface WizardProps {
  steps: WizardStep[];
  onComplete: () => void | Promise<void>;
  onCancel: () => void;
  completeLabel?: string;
  submitting?: boolean;
}

export default function Wizard({
  steps,
  onComplete,
  onCancel,
  completeLabel = 'Submit',
  submitting = false,
}: WizardProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const current = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const canAdvance = current?.isValid() === true;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 4 }}>
        {steps.map((s, i) => (
          <div
            key={s.key}
            style={{
              flex: 1,
              height: 4,
              background: i <= stepIndex ? 'var(--accent)' : 'var(--border-subtle)',
              borderRadius: 2,
              transition: 'background 0.15s',
            }}
          />
        ))}
      </div>

      {/* Step headers */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary)' }}>
        <div>
          Step {stepIndex + 1} of {steps.length}
        </div>
        <div>{current?.title}</div>
      </div>

      {/* Current step title + description */}
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{current?.title}</div>
        {current?.description && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{current.description}</div>
        )}
      </div>

      {/* Step body */}
      <div>{current?.render()}</div>

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
        <div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '8px 16px', fontSize: 13,
              background: 'transparent', color: 'var(--text-secondary)',
              border: '1px solid var(--border-subtle)', borderRadius: 4,
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >Cancel</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={() => setStepIndex(i => Math.max(0, i - 1))}
              disabled={submitting}
              style={{
                padding: '8px 16px', fontSize: 13,
                background: 'transparent', color: 'var(--text-primary)',
                border: '1px solid var(--border-subtle)', borderRadius: 4,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >Back</button>
          )}
          {isLast ? (
            <button
              type="button"
              onClick={() => onComplete()}
              disabled={!canAdvance || submitting}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                background: canAdvance ? 'var(--positive)' : 'var(--bg-hover)',
                color: canAdvance ? 'white' : 'var(--text-hint)',
                border: 'none', borderRadius: 4,
                cursor: submitting ? 'wait' : (canAdvance ? 'pointer' : 'not-allowed'),
              }}
            >{submitting ? 'Submitting…' : completeLabel}</button>
          ) : (
            <button
              type="button"
              onClick={() => setStepIndex(i => Math.min(steps.length - 1, i + 1))}
              disabled={!canAdvance}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                background: canAdvance ? 'var(--accent)' : 'var(--bg-hover)',
                color: canAdvance ? 'white' : 'var(--text-hint)',
                border: 'none', borderRadius: 4,
                cursor: canAdvance ? 'pointer' : 'not-allowed',
              }}
            >Next</button>
          )}
        </div>
      </div>
    </div>
  );
}
